// --------------------------------------------------------------------------
// Body of CPT page
// --------------------------------------------------------------------------

import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios, { AxiosError } from 'axios';
import axiosAuthInstance from '../../context/security';
import { fromArrayBuffer } from 'geotiff';
import { Feature, FeatureCollection } from 'geojson';
import colormap from 'colormap';
import moment from 'moment';
import { Timeline, DataSet, TimelineItem } from 'vis-timeline/standalone';
import 'vis-timeline/styles/vis-timeline-graph2d.min.css';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { Box, Flex, Spacer, IconButton, Heading, Text, Input, Textarea,
  Tabs, Tab, TabList, TabPanels, TabPanel, StatLabel, StatNumber,
  Button, useDisclosure, NumberInput, useToast, RadioGroup, Stat, 
  NumberInputField, InputGroup, InputRightAddon, FormControl, FormLabel, Divider,
  Grid, Spinner, Card, CardBody, CardHeader, Checkbox, Table, Thead, Tbody, Tr, Th,
  Td, Badge, HStack, VStack, ButtonGroup, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Tooltip, useTheme, 
  StatHelpText, Radio, Slider, SliderMark, SliderTrack, SliderFilledTrack,
  SliderThumb, Switch} from "@chakra-ui/react";
import { Map as MapIcon, ChevronRight, ChevronLeft,
  PlayCircleOutline, Circle, BubbleChart } from "@mui/icons-material";
import HistogramCPT from './HistogramCPT';
import HeatmapLegendCPT from './HeatmapLegendCPT';
import HeatmapCircleCPT from './HeatmapCircleCPT';
import MapCPT from './MapCPT';
import { useCPTContext } from '../../context/cpt_context';
import "../styles.css";


const BodyCPT: React.FC = () => {

  // 1. State, hooks, event handling and helper functions Body
  const theme = useTheme();
  const toast = useToast();
  const [tabIndex, setTabIndex] = useState(0);
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const sidebarWidth = "300px";
  const { isOpen: isExportModalOpen, onOpen: onExportModalOpen, onClose: onExportModalClose } = useDisclosure()
  const {geojsonDataInput, errorInputGeoJSON,
    handleGeoJSONChange, startDateInput,
    handleStartDateChange, endDateInput, handleEndDateChange, 
    resolutionInput, handleResolutionChange, srtInput, handleSRTChange,
    selectedPlatforms, satData, searchPlatforms, handlePlatformSearchChange, filteredPlatforms,
    handleSelectFilteredPlatforms, handlePlatformChange, checkAllPlatforms,
    handleSelectAllPlatformsChange, selectedSpectra, checkAllSpectra, spectraData,
    handleSelectAllSpectraChange, handleSpectraChange,
    dropdownVisible, searchRef, errorDB, cloudInput, handleCloudChange,
    mapRef } = useCPTContext();
  const [isSubmittingRequestHeatmap, setIsSubmittingRequestHeatmap] = useState(false);
  const [heatmapDisplayed, setHeatmapDisplayed] = useState(false);
  const [heatmapInputData, setHeatmapInputData] = useState<FeatureCollection | null>(null);
  const [responseHeatmapUrl, setResponseHeatmapUrl] = useState<string | null>(null);
  const [minValueHeatmap, setMinValueHeatmap] = useState<number>(0);
  const [maxValueHeatmap, setMaxValueHeatmap] = useState<number>(0);
  const [responseHeatmapBounds, setResponseHeatmapBounds] = useState<[[number, number],
        [number, number], [number, number], [number, number]] | null>(null);
  const [isSubmittingRequestCPT, setIsSubmittingRequestCPT] = useState(false);
  const [responseSuccessCPT, setResponseSuccessCPT] = useState(false);
  const [responseDataCPT, setResponseDataCPT] = useState<ResponseDataCPT>({
      	 area_of_interest: {}, end_date: "", events: {},
         fields_of_regard: { type: "FeatureCollection", features: [] },
         n_events: 0, start_date: ""});
  const [filteredResults, setFilteredResults] = useState<{ [key: number]: ResultItem }>({});
  const [selectedResultId, setSelectedResultId] = useState<number | null>(null);
  const [resultDisplayed, setResultDisplayed] = useState(false);
  const [sliderCovRate, setSliderCovRate] = useState(0);
  const [showTooltipCovRate, setShowTooltipCovRate] = useState(false);
  const [exportNameInput, setExportNameInput] = useState("");
  const [exportOption, setExportOption] = useState("json");
  const [errorRequest, setErrorRequest] = useState('');
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const timelineInstance = useRef<Timeline | null>(null);
  const minClusteringThreshold = 1000 * 60 * 60 * 4;
  const maxClusteringThreshold = 1000 * 60 * 60 * 24 * 30;
  const [minTimelineDate, setMinTimelineDate] = useState(() => {
    const defaultMinTimelineDate = new Date();
    defaultMinTimelineDate.setDate(defaultMinTimelineDate.getDate() - 30);
    return defaultMinTimelineDate
  });
  const [maxTimelineDate, setMaxTimelineDate] = useState(() => {
    const defaultMaxTimelineDate = new Date();
    defaultMaxTimelineDate.setDate(defaultMaxTimelineDate.getDate() + 365);
    return defaultMaxTimelineDate
  });

  // Handle toggle of sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  // Response data CPT
  interface ResponseDataCPT {
    area_of_interest: {};
    end_date: string;
    events: { [key: number]: ResultItem }; 
    fields_of_regard: FeatureCollection;
    n_events: number;
    start_date: string;
  }

  interface ResultItem {
    platform: string;
    object_id: string;
    mode_name: string;
    spectra: string;
    resolution_m: number | string;
    time_start: string;
    lon_lat_start: Array<number>;
    time_end: string;
    lon_lat_end: Array<number>;
    coverage_rate: number;
    clouds: number | null;
    srt_info: Array<number> | null;
    ground_track: FeatureCollection;
    mid_ground_track: Feature;
    for_geojson: Feature;
  }

  // Handle submit to backend CPT Plan endpoint
  const handleSubmitCPT = async () => {
    setIsSubmittingRequestCPT(true);
    setErrorRequest('');
    setResponseSuccessCPT(false);
    setResponseHeatmapUrl(null);
    setResponseHeatmapBounds(null);
    setTabIndex(1);

    // Get only selected platforms and spectra
    const platformsInput = Object.entries(selectedPlatforms)
    .filter(([_, isSelected]) => isSelected)
    .map(([key, _]) => key);

    const spectraInput = Object.entries(selectedSpectra)
    .filter(([_, isSelected]) => isSelected)
    .map(([key, _]) => key);

    // Allow null or a number for min_resolution
    const minResolution = resolutionInput ?? null;
    console.log(minResolution)
    
    try {
      const response = await axiosAuthInstance.post('/cpt/plan', {
        area_of_interest: geojsonDataInput,
        start_date: startDateInput,
        end_date: endDateInput,
        platforms: platformsInput,
        spectral_range: spectraInput,
        sensor_class: "",
        min_resolution: minResolution,
        weather_info: cloudInput,
        ground_track_info: true,
        srt_info: srtInput
      });
      
      // Handle the response data and initially define filtered results
      setResponseDataCPT(response.data);
      console.log(response.data);
      setFilteredResults(response.data.events);
      setHeatmapInputData(response.data.fields_of_regard)
      setResponseSuccessCPT(true);
    
    } catch (error) {
      // Handle errors
      let errorMessage = 'An unexpected error occurred';
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ErrorResponse>;
        errorMessage = axiosError.message;
        if (axiosError.response && axiosError.response.statusText){
          errorMessage += `: ${axiosError.response.statusText}`;
        }
        if (axiosError.response && axiosError.response.data &&
            axiosError.response.data.detail &&
            axiosError.response.data.detail.length > 0) {
          errorMessage += ` - ${axiosError.response.data.detail}`;
        }  
      }
      setErrorRequest(errorMessage);
      toast({
        title: "API Error.",
        description: errorMessage,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingRequestCPT(false);
    }
  };

  // Handle submit to backend CPT Heatmap endpoint
  const handleSubmitHeatmap = async () => {
    // Check if results already exist
    if (responseHeatmapUrl && responseHeatmapBounds) {
      setHeatmapDisplayed(true);
      handleHeatmapDisplay(responseHeatmapUrl, responseHeatmapBounds);
    } else {
      // Send request to backend endpoint
      setIsSubmittingRequestHeatmap(true);
      setErrorRequest('');
      setHeatmapDisplayed(false);

      try {
        const response = await axiosAuthInstance.post('/cpt/heatmap', {
          area_of_interest: geojsonDataInput,
          fields_of_regard: heatmapInputData,
        }, { responseType: 'blob' });
      
        // Handle the response data
        const arrayBuffer = await response.data.arrayBuffer();
        const tiff = await fromArrayBuffer(arrayBuffer);
        const image = await tiff.getImage();
        const width = image.getWidth();
        const height = image.getHeight();
        const bbox = image.getBoundingBox();
        const bounds: [[number, number], [number, number], [number, number], [number, number]] = [
          [bbox[0], bbox[3]],
          [bbox[2], bbox[3]],          
          [bbox[2], bbox[1]],
          [bbox[0], bbox[1]], 
        ];
        const raster = await image.readRasters({ interleave: true }) as Uint16Array;

        // Normalize uint16 values to 0–255 for browser display
        let min = raster[0];
        let max = raster[0];
        for (let i = 1; i < raster.length; i++) {
          if (raster[i] < min) min = raster[i];
          if (raster[i] > max) max = raster[i];
        }
        setMinValueHeatmap(min);
        setMaxValueHeatmap(max);
        const scale = (val: number) => {
          if (max === min) return 0;
          return ((val - min) / (max - min)) * 255;
        };

        // Draw to canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        const imageData = ctx.createImageData(width, height);
        
        // Define colormap
        const cmap = colormap({
          colormap: 'bone',
          format: 'rgba',
          nshades: 256,
          alpha: 1
        });

        // Apply colormap
        for (let i = 0; i < raster.length; i++) {
          const index = Math.min(255, Math.max(0, Math.round(scale(raster[i]))));
          const color = cmap[index];
          imageData.data[i * 4 + 0] = color[0];
          imageData.data[i * 4 + 1] = color[1];
          imageData.data[i * 4 + 2] = color[2];
          imageData.data[i * 4 + 3] = Math.round(color[3] * 255);
        }

        // Save image & metadata
        ctx.putImageData(imageData, 0, 0);
        const heatmapDataUrl = canvas.toDataURL('image/png');
        setResponseHeatmapUrl(heatmapDataUrl);
        setResponseHeatmapBounds(bounds);
        setHeatmapDisplayed(true);

        // Trigger display on map if data exists
        if (heatmapDataUrl) {
          handleHeatmapDisplay(heatmapDataUrl, bounds);
        }
      
      } catch (error) {
        // Handle errors
        let errorMessage = 'An unexpected error occurred';
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError<ErrorResponse>;
          errorMessage = axiosError.message;
          if (axiosError.response && axiosError.response.statusText){
            errorMessage += `: ${axiosError.response.statusText}`;
          }
          if (axiosError.response && axiosError.response.data &&
            axiosError.response.data.detail &&
            axiosError.response.data.detail.length > 0) {
              errorMessage += ` - ${axiosError.response.data.detail}`;
          }  
        }
        setErrorRequest(errorMessage);
        toast({
          title: "API Error.",
          description: errorMessage,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setIsSubmittingRequestHeatmap(false);
      }
    }
  };

  // Handle error response
  interface ErrorDetail {
    msg: string;
  }
  interface ErrorResponse {
    message: string;
    detail?: ErrorDetail[];
  }

  // Handle filtering based on coverage rate
  // TODO implement cases for multiple filters
  const handleFilter = (threshold: number, allEvents: {[key: number]: ResultItem}) => {
    const filtered = Object.values(allEvents).filter(event => event.coverage_rate >= threshold);
    setFilteredResults(filtered);
    setSelectedResultId(null);
  };

  // Function to animate coverage event
  //const animateCoverageEvent = useCallback((
  //  resultGeoJSON: Feature, resultIndex: number,
  //  flightPathStart: Array<number>, flightPathEnd: Array<number>
  //  ) => {
  //  setSelectedResultId(resultIndex);
  //
  //  const animate = () => {
  //  };
  //
  //  animate();
  //}, [mapRef]);

  // Handle display of field of regard
  const handleCoverageDisplay = useCallback((
    resultGeoJSON: Feature, groundTrackGeoJSON: FeatureCollection,
    midGroundTrack: Feature, resultIndex: number) => {
    if (mapRef.current && mapRef.current.isStyleLoaded()) {

      // Check if one event layer already exists and remove it
      if (mapRef.current.getLayer('FoRPath-layer')) {
        mapRef.current.removeLayer('FoRPath-layer');
        mapRef.current.removeLayer('FoRPath-layer-outline');
      }
      if (mapRef.current.getSource('FoRPath-source')) {
        mapRef.current.removeSource('FoRPath-source');
      }
      if (mapRef.current.getLayer('ground-track-layer')) {
        mapRef.current.removeLayer('ground-track-layer');
      }
      if (mapRef.current.getSource('ground-track-source')) {
        mapRef.current.removeSource('ground-track-source');
      }
      if (mapRef.current.getLayer('ground-track-arrow')) {
        mapRef.current.removeLayer('ground-track-arrow');
      }
      if (mapRef.current.getSource('ground-track-arrow-source')) {
        mapRef.current.removeSource('ground-track-arrow-source');
      }
      
      // Define the data to display
      mapRef.current.addSource('ground-track-source', {
        type: 'geojson',
        data: groundTrackGeoJSON,
      });
      mapRef.current.addSource('ground-track-arrow-source', {
        type: 'geojson',
        data: midGroundTrack,
      });
      mapRef.current.addSource('FoRPath-source', {
        type: 'geojson',
        data: resultGeoJSON,
      });
      
      // Define how to display the data
      mapRef.current.addLayer({
        id: 'ground-track-layer',
        type: 'line',
        source: 'ground-track-source',
        paint: {
              'line-color': "#E53E3E",
              'line-width': 2,
        },
      });  
      mapRef.current.addLayer({
        id: 'ground-track-arrow',
        type: 'symbol',
        source: 'ground-track-arrow-source',
        layout: {
          'icon-image': 'arrow-icon',
          'icon-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            4, 0.275,
            10, 0.55,
            16, 1.1
          ],
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
        },
      });
      mapRef.current.addLayer({
        id: 'FoRPath-layer',
        type: 'fill',
        source: 'FoRPath-source',
        paint: {
          'fill-color': "#cbd5e0",
          'fill-opacity': 0.8,
        },
      });
      mapRef.current.addLayer({
        id: 'FoRPath-layer-outline',
        type: 'line',
        source: 'FoRPath-source',
        paint: {
          'line-color': "#cbd5e0",
          'line-width': 2,
        },
      });  
    }
    setSelectedResultId(resultIndex);
    setResultDisplayed(true);
  }, [mapRef]);
    
    // Handle display of heatmap
    const handleHeatmapDisplay = useCallback((
      heatmapUrl: string, bounds: [[number, number], [number, number], [number, number], [number, number]]) => {
      if (mapRef.current && mapRef.current.isStyleLoaded()) {

        // Check if one event layer already exists and remove it
        if (mapRef.current.getLayer('FoRHeatmap-layer')) {
          mapRef.current.removeLayer('FoRHeatmap-layer');
        }
        if (mapRef.current.getSource('FoRHeatmap-source')) {
          mapRef.current.removeSource('FoRHeatmap-source');
        }
  
        // Add the tile layer from the TileJSON object
        mapRef.current.addSource('FoRHeatmap-source', {
          type: 'image',
          url: heatmapUrl,
          coordinates: bounds,
        });
  
        // Define how to display the data
        mapRef.current.addLayer({
          id: 'FoRHeatmap-layer',
          type: 'raster',
          source: 'FoRHeatmap-source',
          paint: {
            'raster-opacity': 0.7,
          },
        });
      }
    }, [mapRef]);

  // Remove heatmap actively
  const removeHeatmap = useCallback(() => {
    if (mapRef.current && mapRef.current.isStyleLoaded()) {

      // Check if layer and source already exist and remove it
      if (mapRef.current.getLayer('FoRHeatmap-layer')) {
        mapRef.current.removeLayer('FoRHeatmap-layer');
      }
      if (mapRef.current.getSource('FoRHeatmap-source')) {
        mapRef.current.removeSource('FoRHeatmap-source');
      }
      setHeatmapDisplayed(false)
    }
  }, [mapRef]);
  
  // Clean-up when switching to parameter/input tab or deselect result
  useEffect(() => {
    if (selectedResultId === null && mapRef.current && mapRef.current.isStyleLoaded()) {
      if (mapRef.current.getLayer('FoRPath-layer')) {
        mapRef.current.removeLayer('FoRPath-layer');
        mapRef.current.removeLayer('FoRPath-layer-outline');
      }
      if (mapRef.current.getSource('FoRPath-source')) {
        mapRef.current.removeSource('FoRPath-source');
      }
      if (mapRef.current.getLayer('ground-track-layer')) {
        mapRef.current.removeLayer('ground-track-layer');
      }
      if (mapRef.current.getSource('ground-track-source')) {
        mapRef.current.removeSource('ground-track-source');
      }
      if (mapRef.current.getLayer('ground-track-arrow')) {
        mapRef.current.removeLayer('ground-track-arrow');
      }
      if (mapRef.current.getSource('ground-track-arrow-source')) {
        mapRef.current.removeSource('ground-track-arrow-source');
      }
      setResultDisplayed(false);
    }

    if (tabIndex !== 1 && mapRef.current && mapRef.current.isStyleLoaded()) {
      if (mapRef.current.getLayer('FoRPath-layer')) {
        mapRef.current.removeLayer('FoRPath-layer');
        mapRef.current.removeLayer('FoRPath-layer-outline');
      }
      if (mapRef.current.getSource('FoRPath-source')) {
        mapRef.current.removeSource('FoRPath-source');
      }
      if (mapRef.current.getLayer('ground-track-layer')) {
        mapRef.current.removeLayer('ground-track-layer');
      }
      if (mapRef.current.getSource('ground-track-source')) {
        mapRef.current.removeSource('ground-track-source');
      }
      if (mapRef.current.getLayer('ground-track-arrow')) {
        mapRef.current.removeLayer('ground-track-arrow');
      }
      if (mapRef.current.getSource('ground-track-arrow-source')) {
        mapRef.current.removeSource('ground-track-arrow-source');
      }
      if (mapRef.current.getLayer('FoRHeatmap-layer')) {
        mapRef.current.removeLayer('FoRHeatmap-layer');
      }
      if (mapRef.current.getSource('FoRHeatmap-source')) {
        mapRef.current.removeSource('FoRHeatmap-source');
      }
      setSelectedResultId(null);
      setResultDisplayed(false);
      setHeatmapDisplayed(false);
    }
  }, [tabIndex, mapRef, selectedResultId]);

  // Function to jump to selected result card
  const scrollToResultCard = (resultIndex: number) => {
    const cardId = `result-card-${resultIndex}`;
    const cardElement = document.getElementById(cardId);
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Format the date to a string without the weekday
  const formatUTC = (utcDateString: string) => {
    const date = new Date(utcDateString);
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'UTC'
    };
    const formattedParts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
    const year = formattedParts.find(part => part.type === 'year')?.value;
    const month = formattedParts.find(part => part.type === 'month')?.value;
    const day = formattedParts.find(part => part.type === 'day')?.value;
    const hour = formattedParts.find(part => part.type === 'hour')?.value;
    const minute = formattedParts.find(part => part.type === 'minute')?.value;
    const second = formattedParts.find(part => part.type === 'second')?.value;
    const formattedString = `${month}/${day}/${year} ${hour}:${minute}:${second} UTC`;
    
    return formattedString;
  };

  // Format datetime with short name for month and without seconds
  const formatUTCAlternative = (utcDateString: string) => {
    const date = new Date(utcDateString);
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC'
    };
    const formattedParts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
    const year = formattedParts.find(part => part.type === 'year')?.value;
    const month = formattedParts.find(part => part.type === 'month')?.value;
    const day = formattedParts.find(part => part.type === 'day')?.value;
    const hour = formattedParts.find(part => part.type === 'hour')?.value;
    const minute = formattedParts.find(part => part.type === 'minute')?.value;
    const formattedString = `${month} ${day}, ${year}, ${hour}:${minute} UTC`;
  
    return formattedString;
  };

  // Format datetime with short name for month and without time
  const formatUTCShort = (utcDateString: string) => {
    const date = new Date(utcDateString);
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
    };
    const formattedParts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
    const year = formattedParts.find(part => part.type === 'year')?.value;
    const month = formattedParts.find(part => part.type === 'month')?.value;
    const day = formattedParts.find(part => part.type === 'day')?.value; 
    const formattedString = `${month} ${day}, ${year}`;
    
    return formattedString;
  };

  // Mapping spectra with colors
  const defineSpectrumColor = (spectrum: string) => {
    switch (spectrum.trim()) {
      case 'EUV':
      case 'UV-C':
      case 'UV-B':
      case 'UV-A':
        return 'red';
      case 'VIS':
      case 'Red-Edge':
        return 'yellow';
      case 'NIR':
      case 'SWIR':
      case 'MWIR':
      case 'TIR':
      case 'FIR':
        return 'green';
      case 'W-Band':
      case 'V-Band':
      case 'Ka-Band':
      case 'K-Band':
      case 'Ku-Band':
      case 'X-Band':
      case 'C-Band':
      case 'S-Band':
      case 'L-Band':
        return 'blue';
      case 'UHF':
      case 'VHF':
      case 'HF':
        return 'purple';
      default:
        return 'gray';
    }
  };

  // Update limits of timeline based on input values for time period
  useEffect(() => {
    // Get date which is in local time
    const startDate = moment(startDateInput);
    // Get and add offset to UTC in order to compensate that the
    // timeline treats it as UTC
    const minDate = startDate.add(startDate.utcOffset(), 'minutes').toISOString();
    setMinTimelineDate(new Date(minDate));
  }, [startDateInput]);

  useEffect(() => {
    // Get date which is in local time
    const startDate = moment(endDateInput);
    // Get and add offset to UTC in order to compensate that the
    // timeline treats it as UTC
    const maxDate = startDate.add(startDate.utcOffset(), 'minutes').toISOString();
    setMaxTimelineDate(new Date(maxDate));
  }, [endDateInput]);

  // Handle Timeline with result items and resizing
  useEffect(() => {
    const onSelect = (properties: any) => {
      const resultIndex = properties.items[0];
      const resultItem = filteredResults[resultIndex];
      if (resultItem) {
        handleCoverageDisplay(resultItem.for_geojson,
                              resultItem.ground_track,
                              resultItem.mid_ground_track,
                              resultIndex);
        scrollToResultCard(resultIndex);
      }
    };
    
    const onZoomChange = (properties: any) => {
      if (timelineInstance.current) {
        if (properties.end - properties.start < minClusteringThreshold) {
          timelineInstance.current.setOptions({ cluster: undefined });
        } else {
          timelineInstance.current.setOptions({
            cluster: {
              clusterCriteria: (firstItem: TimelineItem, secondItem: TimelineItem) => {
                const start1 = firstItem.start instanceof Date ? firstItem.start : new Date(firstItem.start);
                const start2 = secondItem.start instanceof Date ? secondItem.start : new Date(secondItem.start);
                return Math.abs(start1.getTime() - start2.getTime()) < maxClusteringThreshold;
              }
            }
          });
        };
      }
    };
    
    // Initialize the timeline when tab changes to the result
    // tab or the sidebar is opened
    if (tabIndex === 1) {
      if (!timelineInstance.current && timelineRef.current) {

        const timelineItems = Object.values(filteredResults as 
          ResultItem[]).map((event, index) => ({
          id: index,
          content: "",
          start: event.time_start,
          end: event.time_end,
        }));

        const items = new DataSet(timelineItems);
      
        const options = {
          locale: "en",
          locales: {
            en: {}
          },
          preferZoom: true,
          verticalScroll: true,
          orientation: { axis: "top", item: "top" },
          min: minTimelineDate,
          max: maxTimelineDate,
          zoomMin: 1000 * 60 * 60,
          zoomMax: 1000 * 60 * 60 * 24 * 365,
          cluster: {
            clusterCriteria: (firstItem: TimelineItem, secondItem: TimelineItem) => {
              const start1 = firstItem.start instanceof Date ? firstItem.start : new Date(firstItem.start);
              const start2 = secondItem.start instanceof Date ? secondItem.start : new Date(secondItem.start);
              return Math.abs(start1.getTime() - start2.getTime()) < maxClusteringThreshold;
            } 
          },
          moment: function (date: any) {
            return moment.utc(date);
          }
        };

        timelineInstance.current = new Timeline(timelineRef.current, items,
          options);
        timelineInstance.current.on('rangechanged', onZoomChange); 
        timelineInstance.current.on('select', onSelect);
      }
    };

    return () => {
      if (timelineInstance.current) {
        timelineInstance.current.off('select', onSelect);
        timelineInstance.current.off('rangechanged', onZoomChange);
        timelineInstance.current.destroy();
        timelineInstance.current = null;
      }
    };
  }, [filteredResults, tabIndex, minTimelineDate, maxTimelineDate,
    maxClusteringThreshold, minClusteringThreshold, handleCoverageDisplay ]);

  // Handle input for export name 
  const handleExportNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExportNameInput(event.target.value);
  };

  // Handle export of results
  const handleExport = (responseData: ResponseDataCPT,
    exportName: string, exportOption: string) => {
    
    // CSV option
    const exportToCSV = (data: ResponseDataCPT, exportName: string) => {
      const header = [
        "Platform",
        "Object ID",
        "Time Start",
        "Time End",
        "Coverage Rate [%]",
        "Spectra",
        "Resolution [m]",
        "Orbit",
        "Cloud coverage [%]",
      ].map(item => `"${item}"`).join(";") + "\n";
    
      // Convert events object to an array
      const eventsArray = Object.values(data.events);
      const csvContent = eventsArray.map(event => [
        `"${event.platform}"`,                           
        `"${event.object_id}"`,                         
        `"${event.time_start}"`,
        `"${event.time_end}"`,
        `"${event.coverage_rate.toFixed(2)}"`,         
        `"${event.spectra}"`,                           
        `"${event.resolution_m}"`,       
        `"${event.mid_ground_track.properties?.orbit}"`,
        `"${event.clouds}"`,    
      ].join(";")).join("\n");
    
      // Create and encode csv data
      const fullCsv = "data:text/csv;charset=utf-8," + header + csvContent;
      const encodedUri = encodeURI(fullCsv);
      
      // Trigger download
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${exportName}.csv`);
      document.body.appendChild(link);
      link.click();
    };

    // JSON option
    const exportToJSON = (data: ResponseDataCPT, exportName: string) => {
      // Create a deep copy of the original data
      const filteredData = JSON.parse(JSON.stringify(data));

      // Exclude the ground_track from each ResultItem
      for (const key in filteredData.events) {
        if (filteredData.events.hasOwnProperty(key)) {
          delete filteredData.events[key].ground_track;
        }
      }

      // Convert data into json string with pretty print indentation
      const jsonContent = JSON.stringify(filteredData, null, 2);

      // Create blob object with json content
      const blob = new Blob([jsonContent], { type: 'application/json' });

      // Trigger download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `${exportName}.json`);
      document.body.appendChild(link);
      link.click();
    };

    // Markdown option
    const exportToMarkdown = (data: ResponseDataCPT, exportName: string) => {
      const eventsArray = Object.values(data.events);
      let markdownContent = "# Coverage Plan\n\n";

      markdownContent += "### Query:\n";
      markdownContent += "| Parameter | Value |\n";
      markdownContent += "|-----------|-------|\n";
      markdownContent += `| Start Date | ${data.start_date} |\n`;
      markdownContent += `| End Date | ${data.end_date} |\n`;
      markdownContent += `| Area of Interest | ${JSON.stringify(data.area_of_interest)} |\n\n`;

      // Create table header
      markdownContent += "### Results:\n";  
      markdownContent += "| Platform | Object ID | Time Start | Time End | Coverage Rate [%] | Spectra | Resolution [m] | Orbit | Cloud coverage [%] |\n";
      markdownContent += "|----------|-----------|------------|----------|-------------------|---------|----------------|-------|-------|\n";

      // Create table rows
      eventsArray.forEach(event => {
        markdownContent += `| ${event.platform} | ${event.object_id} | ${event.time_start} | ${event.time_end} | ${event.coverage_rate} | ${event.spectra} | ${event.resolution_m} | ${event.mid_ground_track.properties?.orbit} | ${event.clouds} |\n`;
      });

      // Create blob object and trigger download
      const blob = new Blob([markdownContent], { type: 'text/markdown' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `${exportName}.md`);
      document.body.appendChild(link);
      link.click();
    };

    switch (exportOption) {
      case 'json':
        exportToJSON(responseData, exportName);
        break;
      case 'csv':
        exportToCSV(responseData, exportName);
        break;
      case 'md':
        exportToMarkdown(responseData, exportName);
        break;
    }

  };

  // Effect to show toast when errorDB is not empty
  useEffect(() => {
    if (errorDB) {
      toast({
        title: "Database Error.",
        description: errorDB,
        status: "error",
        duration: 20000,
        isClosable: true,
      });
    }
  }, [errorDB, toast]);

  // Effect to show toast when errorInputGeoJSON is not empty
  useEffect(() => {
    if (errorInputGeoJSON) {
      toast({
        title: "Incorrect GeoJSON.",
        description: errorInputGeoJSON,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [errorInputGeoJSON, toast]);
  

  // 2. JSX Body
  return (
    <Flex flex="1" position="relative" overflow="hidden">
      <MapCPT />

      <Button
        onClick={toggleSidebar}
        position="absolute"
        left={isSidebarOpen ? sidebarWidth : "0"} 
        top="50%" transform="translateY(-50%)"
        zIndex="docked"
        pointerEvents="auto"
        borderLeftRadius={0} px={0} shadow="lg">
        {isSidebarOpen ? <ChevronLeft /> : <ChevronRight />}
      </Button>

      <Grid
        position="absolute"
        top="0" left="0"
        h="100%"
        w="100%"
        templateColumns={isSidebarOpen ? `${sidebarWidth} 1fr` : "0 1fr"}
        overflowX="hidden" pointerEvents="none" zIndex="docked">
        <Box
          id="sidebar"
          py={4} px={isSidebarOpen ? 1 : 0}
          width={isSidebarOpen ? sidebarWidth : "0"}
          maxHeight="100%"
          bg={theme.colors.main.box}
          color="white"
          overflowY={isSidebarOpen ? "auto" : "hidden"}
          zIndex="popover"
          transition="width 0.1s ease, padding 0.1s ease"
          pointerEvents="auto">
          <Tabs
            variant='soft-rounded'
            index={tabIndex} onChange={(index) => setTabIndex(index)}>
            <TabList>
              <Tab
                sx={{_selected: {
                  color: theme.colors.main.highlightDark,
                  bg: theme.colors.main.highlightLight,
                  },
                  color: "white", bg: "transparent", fontFamily: "label",
                  fontWeight:"400"}}>
                Parameters
              </Tab>
              <Tab
                sx={{_selected: {
                  color: theme.colors.main.highlightDark,
                  bg: theme.colors.main.highlightLight,
                  },
                  color: "white", bg: "transparent", fontFamily: "label",
                  fontWeight:"400"}}>
                Results
              </Tab>
            </TabList>
            <TabPanels>
              
              {/* Input or Parameter Tab */}
              <TabPanel>
                <FormControl isRequired>              
                  <FormLabel
                    mb={2}
                    fontSize={theme.fontSize.cardName}>
                    01 Area of Interest
                  </FormLabel>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Define your area of interest as a GeoJSON Feature
                    or draw your area directly on the map.
                    Note that you can only draw one feature.
                  </Text>
                  <Textarea
                    placeholder='{"type": "Feature","geometry": {}}'
                    height="200px"
                    className="discreet-scrollbar"
                    value={geojsonDataInput ? JSON.stringify(geojsonDataInput, null, 2) : ''}
                    onChange={handleGeoJSONChange}
                    isRequired={true}/>
                </FormControl>
                <Divider mt={6} mb={4}/>
                <FormControl isRequired>
                  <FormLabel
                    mb={2}
                    fontSize={theme.fontSize.cardName}>
                    02 Period of Time
                  </FormLabel>
                  <Text
                    mb={2}
                    fontFamily="label" fontSize={theme.fontSize.body}>
                    Start Date [UTC]
                  </Text>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Select the date for the beginning of the coverage
                    analysis from the calender.
                  </Text>
                  <Input mb={4}
                    type="datetime-local" isRequired={true}
                    value={startDateInput}
                    onChange={handleStartDateChange}
                    sx={{
                      '::-webkit-calendar-picker-indicator': {
                        filter: 'invert(1)',
                      },
                    }}/>
                  <Text
                    mb={2}
                    fontFamily="label" fontSize={theme.fontSize.body}>
                    End Date [UTC]
                  </Text>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Select the date for the end of the coverage
                    analysis from the calender.
                  </Text>
                  <Input
                    type="datetime-local" isRequired={true}
                    value={endDateInput}
                    onChange={handleEndDateChange}
                    sx={{
                      '::-webkit-calendar-picker-indicator': {
                        filter: 'invert(1)',
                      },
                    }}/>
                </FormControl>
                <Divider mt={6} mb={4}/>
                <FormControl>
                  <FormLabel
                    mb={2}
                    fontSize={theme.fontSize.cardName}>
                    03 Platforms
                  </FormLabel>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Select all Earth Observation platforms
                    that shall be considered. If none is selected,
                    all platforms are considered.
                  </Text>
                  <Box position="relative" mb={2} ref={searchRef}>
                    <Input
                      placeholder="Search platforms..." value={searchPlatforms}
                      onChange={handlePlatformSearchChange} mb={2}/>
                    {searchPlatforms && dropdownVisible && (
                      <Box
                        color="white"
                        position="absolute"
                        p={2} mt={1}
                        borderRadius="sm" shadow="dark-lg"
                        width="full" zIndex="popover"
                        bg="black" className="discreet-scrollbar"
                        maxH="200px" overflowY="auto">
                        <Checkbox
                          isChecked={filteredPlatforms.every((key) => selectedPlatforms[key])}
                          onChange={handleSelectFilteredPlatforms}>
                          <Text fontSize={theme.fontSize.info}>
                            Select all
                          </Text>
                        </Checkbox>
                        <Divider mb={1}/>
                        {filteredPlatforms.map((platformKey) => (
                          <Box key={platformKey}>
                            <Checkbox 
                              isChecked={selectedPlatforms[platformKey]}
                              onChange={(e) => handlePlatformChange(platformKey, e.target.checked)}>
                              <Text fontSize={theme.fontSize.info}>
                                {satData.eo_platforms[platformKey].platform_name}
                              </Text>
                            </Checkbox>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box className="discreet-scrollbar" maxHeight="350px" overflowY="auto">
                    <Table
                      size="xs" colorScheme="blackAlpha">
                      <Thead>
                        <Tr>
                          <Th color="white" borderBottom="1px solid"
                            borderColor="white" pb={1}
                            fontSize={theme.fontSize.info}>
                            <Box display="flex" alignItems="center">
                              <Checkbox
                                width="10%"
                                isChecked={checkAllPlatforms}
                                onChange={handleSelectAllPlatformsChange}/>
                            </Box>
                          </Th>
                          <Th 
                            width="52%" fontSize={theme.fontSize.info}
                            color="white" borderBottom="1px solid" 
                            borderColor="white" pb={1}>
                            Name
                          </Th>
                          <Th
                            width="38%" fontSize={theme.fontSize.info}
                            color="white" borderBottom="1px solid"
                            borderColor="white" pb={1}>
                            ID
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody pt={2}>
                        {Object.entries(satData.eo_platforms).sort(([, platformItemA], [, platformItemB]) => 
                        platformItemA.platform_name.localeCompare(platformItemB.platform_name))
                        .map(([platformKey, platformItem]) => (
                          <Tr key={platformKey}>
                            <Td fontSize={theme.fontSize.info}>
                              <Box display="flex" alignItems="center">
                                <Checkbox
                                  isChecked={selectedPlatforms[platformKey]}
                                  onChange={(e) => handlePlatformChange(platformKey, e.target.checked)}
                                />
                              </Box>
                            </Td>
                            <Td fontSize={theme.fontSize.info}>{platformItem.platform_name}</Td>
                            <Td fontSize={theme.fontSize.info}>{platformKey}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </FormControl>
                {errorDB && <Text fontSize={theme.fontSize.info} mt={2} color={theme.colors.main.warning}>{errorDB}</Text>}
                <Divider mt={6} mb={4}/>
                <FormControl>
                  <FormLabel
                    mb={2} mt={2}
                    fontSize={theme.fontSize.cardName}>
                    04 Spectral Range
                  </FormLabel>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Define all spectral ranges of interest. If none is selected,
                    all are considered.
                  </Text>
                  <Checkbox
                    isChecked={checkAllSpectra}
                    onChange={handleSelectAllSpectraChange}>
                    <Text fontSize={theme.fontSize.info}>
                      Select All
                    </Text>
                  </Checkbox>
                  <Box mb={4} className="discreet-scrollbar" maxHeight="450px" overflowY="auto">
                    <Table 
                      size="xs" colorScheme="blackAlpha">
                      <Thead position="sticky" top="0" zIndex="1">
                        <Tr>
                          <Th color="white" borderBottom="1px solid"
                            borderColor="white" pb={1}
                            fontSize={theme.fontSize.info}>
                            EO/IR
                          </Th>
                          <Th color="white" borderBottom="1px solid" 
                            borderColor="white" pb={1}
                            fontSize={theme.fontSize.info}>
                            Name
                          </Th>
                          <Th color="white" borderBottom="1px solid" 
                            borderColor="white" pb={1} isNumeric
                            fontSize={theme.fontSize.info}>
                            Max Lambda [nm]
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody pt={2}>
                        {Object.entries(spectraData.spectra)
                        .filter(([_, spectraItem]) => spectraItem.cat === 'EO/IR')
                        .map(([spectraKey, spectraItem]) => (
                          <Tr key={spectraKey}>
                            <Td fontSize={theme.fontSize.info}>
                              <Box display="flex" alignItems="center">
                                <Checkbox
                                  isChecked={selectedSpectra[spectraKey]}
                                  onChange={(e) => handleSpectraChange(spectraKey, e.target.checked)}/>
                              </Box>
                            </Td>
                            <Td fontSize={theme.fontSize.info}>{spectraKey}</Td>
                            <Td fontSize={theme.fontSize.info} isNumeric>{spectraItem.specs}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  {errorDB && <Text fontSize={theme.fontSize.info} mt={2} color={theme.colors.main.warning}>{errorDB}</Text>}
                  <Box className="discreet-scrollbar" maxHeight="500px" overflowY="auto">
                    <Table
                      size="xs" colorScheme="blackAlpha">
                      <Thead position="sticky" top="0" zIndex="1">
                        <Tr>
                          <Th color="white" borderBottom="1px solid"
                            borderColor="white" pb={1}
                            fontSize={theme.fontSize.info}>
                            Radio
                          </Th>
                          <Th color="white" borderBottom="1px solid" 
                            borderColor="white" pb={1}
                            fontSize={theme.fontSize.info}>
                            Name
                          </Th>
                          <Th color="white" borderBottom="1px solid" 
                            borderColor="white" pb={1} isNumeric
                            fontSize={theme.fontSize.info}>
                            Min Freq [MHz]
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody pt={2}>
                        {Object.entries(spectraData.spectra)
                        .filter(([_, spectraItem]) => spectraItem.cat === 'Radio')
                        .map(([spectraKey, spectraItem]) => (
                          <Tr key={spectraKey}>
                            <Td fontSize={theme.fontSize.info}>
                              <Box display="flex" alignItems="center">
                                <Checkbox
                                  isChecked={selectedSpectra[spectraKey]}
                                  onChange={(e) => handleSpectraChange(spectraKey, e.target.checked)}/>
                              </Box>
                            </Td>
                            <Td fontSize={theme.fontSize.info}>{spectraKey}</Td>
                            <Td fontSize={theme.fontSize.info} isNumeric>{spectraItem.specs}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </FormControl>
                {errorDB && <Text fontSize={theme.fontSize.info} mt={2} color={theme.colors.main.warning}>{errorDB}</Text>}
                <Divider mt={6} mb={4}/>
                <FormControl>
                  <FormLabel
                    mb={2} mt={2}
                    fontSize={theme.fontSize.cardName}>
                    05 Resolution
                  </FormLabel>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Specify your minimal required resolution.
                  </Text>
                  <InputGroup>
                    <NumberInput
                      width="100%" min={0} keepWithinRange={true}
                      value={resolutionInput ?? undefined}
                      step={0.1}
                      onChange={handleResolutionChange}>
                      <NumberInputField
                      borderTopRightRadius="none"
                      borderBottomRightRadius="none"/>
                    </NumberInput>
                    <InputRightAddon width="auto" color="black">
                      m
                    </InputRightAddon>
                  </InputGroup>
                </FormControl>
                <Divider mt={6} mb={4}/>
                <FormControl>
                  <FormLabel
                    mb={2} mt={2}
                    fontSize={theme.fontSize.cardName}>
                    06 Options
                  </FormLabel>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Add optional analyses.
                  </Text>
                  <HStack>
                    <Switch
                      isChecked={srtInput}
                      onChange={handleSRTChange} />
                    <Text fontSize={theme.fontSize.body}>
                      System Response Time (Leaf Space)
                    </Text>
                  </HStack>
                  <HStack>
                    <Switch
                      isChecked={cloudInput}
                      onChange={handleCloudChange} />
                    <Text fontSize={theme.fontSize.body}>
                      Cloud Forecast (1 Week)
                    </Text>
                  </HStack>
                </FormControl>
                <VStack align="left">
                  <Button
                    mt={8} onClick={handleSubmitCPT}
                    isLoading={isSubmittingRequestCPT}
                    loadingText="Processing">
                    Submit
                  </Button>
                </VStack>
              </TabPanel>

              {/* Result Tab */}
              <TabPanel>
                <Text 
                  mb={2}
                  fontFamily="label" fontSize={theme.fontSize.body}>
                  Coverage Events
                </Text>
                <HStack>
                  <Circle
                    sx={{ color: 'black' }} 
                    fontSize={theme.fontSize.iconSingle}/>
                  <Text fontSize={theme.fontSize.body}>
                    Area of Interest
                  </Text>
                </HStack>
                {resultDisplayed &&
                <HStack>
                  <Circle
                    sx={{ color: "#cbd5e0" }}
                    fontSize={theme.fontSize.iconSingle}/>
                  <Text fontSize={theme.fontSize.body}>
                    Field of Regard over Time
                  </Text>
                </HStack>}
                {resultDisplayed &&
                <HStack>
                  <Circle
                    sx={{ color: "#E53E3E" }}
                    fontSize={theme.fontSize.iconSingle}/>
                  <Text fontSize={theme.fontSize.body}>
                    Ground Track
                  </Text>
                </HStack>}
                {heatmapDisplayed &&
                <HStack mb={1}>
                  <HeatmapCircleCPT />
                  <Text fontSize={theme.fontSize.body}>
                    Heatmap Events
                  </Text>
                </HStack>}
                {heatmapDisplayed && 
                <HeatmapLegendCPT
                  min={minValueHeatmap}
                  max={maxValueHeatmap} />}

                {/* Request not send yet */}
                {responseSuccessCPT === false && (
                  <Card
                  display="flex"
                  borderRadius={theme.radii.card}
                  justifyContent="center" alignItems="center"
                  mt={2} width="100%"
                  bg={theme.colors.main.results}
                  border="1px">
                    <CardBody>
                      {isSubmittingRequestCPT === false && (
                      <Text color="black" fontSize={theme.fontSize.info}>
                        No Results. Send Request.
                      </Text>
                      )}
                      {isSubmittingRequestCPT && <Spinner color='black' />} 
                    </CardBody>
                  </Card>
                )}

                {/* Request failed */}
                {errorRequest && 
                  <Text
                    mt={2}
                    fontSize={theme.fontSize.info}
                    color={theme.colors.main.warning}>
                    {errorRequest}
                  </Text>
                }

                {/* Request success */}
                {responseSuccessCPT && responseDataCPT && filteredResults && (
                  <Box>
                    {/* General Stats */}
                    <Divider my={4} />
                    <Stat>
                      <StatLabel
                        fontFamily={"label"}
                        color={theme.colors.main.results}
                        fontSize={theme.fontSize.body}>
                        Number of Events
                      </StatLabel>
                      <StatNumber>{Object.keys(filteredResults).length} of {responseDataCPT.n_events}</StatNumber>
                      <StatHelpText
                        fontSize={theme.fontSize.info}>
                        {formatUTCShort(startDateInput)} - {formatUTCShort(endDateInput)}
                      </StatHelpText>
                    </Stat>
                    <Text
                      mt={6} mb={1} variant={"results"} fontFamily={"label"}
                      fontSize={theme.fontSize.body}>
                      Events per Hour of Day [UTC]
                    </Text>
                    <HistogramCPT data={Object.values(filteredResults as ResultItem[])} />
                    <Flex justify="space-between" align="center">
                      <Text variant={"results"} fontSize={theme.fontSize.info}>0 AM</Text>
                      <Text variant={"results"} fontSize={theme.fontSize.info}>12 AM</Text>
                    </Flex>
                    <Text
                      mt={6} mb={1} variant={"results"} fontFamily={"label"}
                      fontSize={theme.fontSize.body}>
                      Minimum Coverage Rate
                    </Text>
                    <Slider mb={2}
                      id='sliderCovRate' defaultValue={0}
                      onChangeEnd={(value) => {setSliderCovRate(value); handleFilter(value, responseDataCPT.events)}}
                      onMouseEnter={() => setShowTooltipCovRate(true)}
                      onMouseLeave={() => setShowTooltipCovRate(false)}>
                      <SliderMark
                        value={0} mt='1'
                        fontSize={theme.fontSize.info}>
                        0%
                      </SliderMark>
                      <SliderMark
                        value={100} mt='1' ml='-8'
                        fontSize={theme.fontSize.info}>
                        100%
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <Tooltip
                        hasArrow bg='black' fontSize={theme.fontSize.info}
                        color='white' placement='bottom'
                        isOpen={showTooltipCovRate} label={`${sliderCovRate}%`}>
                        <SliderThumb />
                      </Tooltip>
                    </Slider>
                    <Divider my={4} />
                    <Text
                      mt={0} variant={"results"} fontFamily={"label"}
                      fontSize={theme.fontSize.body}>
                      Add to Map
                    </Text>
                    <Flex>
                      <HStack>
                        <BubbleChart />
                        <Text
                          variant={"results"} fontSize={theme.fontSize.body}>
                          Heatmap Layer
                        </Text>
                      </HStack>
                      <Spacer />
                      {isSubmittingRequestHeatmap === false && heatmapDisplayed === false && (
                        <Text onClick={handleSubmitHeatmap} cursor="pointer"
                          fontSize={theme.fontSize.info}>
                          Add
                        </Text>
                      )}
                      {isSubmittingRequestHeatmap && <Spinner size={'sm'} color={theme.colors.main.white} />}
                      {isSubmittingRequestHeatmap === false && heatmapDisplayed && (
                        <Text onClick={removeHeatmap} cursor="pointer"
                          fontSize={theme.fontSize.info}>
                          Remove
                        </Text>
                      )}
                    </Flex>
                    {/* Filtered Results */}
                    {Object.keys(filteredResults).length > 0 && (
                      <Box>
                        {/* List of events */}
                        <Divider my={4} />
                        <Text
                          mb={1}
                          variant={"results"} fontFamily={"label"}
                          fontSize={theme.fontSize.body}>
                          List ({Object.keys(filteredResults).length} Events)
                        </Text>
                        <Box
                          className="discreet-scrollbar"
                          maxHeight={`290px`}
                          overflowY="auto">
                          {Object.values(filteredResults as ResultItem[]).map((event, eventIndex) => (
                            <Card
                              id={`result-card-${eventIndex}`}
                              key={eventIndex}
                              bg={selectedResultId === eventIndex ? theme.colors.main.selected : theme.colors.main.results}
                              borderRadius={theme.radii.card}
                              cursor={"pointer"}
                              _hover={{bg: theme.colors.main.selected}}
                              onClick={() => {
                                setSelectedResultId(eventIndex);}}
                              width="100%" mb={2}>
                              <CardHeader px={2} py={2}>
                                <Flex align="center">
                                  <Heading
                                    color={"black"}
                                    fontSize={theme.fontSize.cardName}>
                                    {event.platform}
                                  </Heading>
                                  <Spacer />
                                  <ButtonGroup spacing={1} isAttached>
                                    <Tooltip
                                      label="Animate Coverage - Not Implemented" aria-label="Animate FoR"
                                      placement="top" hasArrow bg="Black">
                                      <IconButton
                                        icon={<PlayCircleOutline fontSize={theme.fontSize.iconSingle} />}
                                        borderColor="black" bg="gray.50"
                                        //onClick={() => animateCoverageEvent(event.for_geojson,
                                        //                                   eventIndex,
                                        //                                   event.lon_lat_start, event.lon_lat_end)}
                                        variant={"outline"}
                                        aria-label="Animate Coverage Event">
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip
                                      label="Show Coverage over Time" aria-label="Show FoRPath"
                                      placement="top" hasArrow bg="Black">
                                      <IconButton
                                        icon={<MapIcon fontSize={theme.fontSize.iconSingle} />}
                                        borderColor="black" bg="gray.50"
                                        onClick={() => handleCoverageDisplay(event.for_geojson,
                                                                             event.ground_track,
                                                                             event.mid_ground_track,
                                                                             eventIndex)}
                                        variant={"outline"}
                                        aria-label="Show Coverage over Time">
                                      </IconButton>
                                    </Tooltip>
                                  </ButtonGroup>
                                </Flex>
                                <Text
                                  color={"black"}
                                  fontSize={theme.fontSize.info}>
                                  Mode: {event.mode_name}
                                </Text>
                                <Text
                                  color={"black"}
                                  fontSize={theme.fontSize.info}>
                                  {formatUTCAlternative(event.time_start)}
                                </Text>
                                {event.spectra.split(',').map((spectrum, index) => (
                                  <Badge 
                                    key={index} 
                                    ml={index > 0 ? 2 : 0} 
                                    colorScheme={defineSpectrumColor(spectrum)}
                                    fontSize={"0.7rem"}>
                                    {spectrum.trim()}
                                  </Badge>
                                ))}
                              </CardHeader>
                            </Card>
                          ))}
                        </Box>
                        {/* Details */}
                        <Text
                          mt={4} mb={1} variant={"results"} fontFamily={"label"}
                          fontSize={theme.fontSize.body}>
                          Details
                        </Text>
                        {selectedResultId !== null ? (
                          (() => {
                            const selectedEvent = Object.values(filteredResults as ResultItem[])[selectedResultId];
                            return (
                              <Grid templateColumns="auto 1fr" gap={1}>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  Platform:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak="break-all" display="inline">
                                  {selectedEvent.platform}
                                </Text>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  ID:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak="break-all" display="inline">
                                  {selectedEvent.object_id}
                                </Text>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  Orbit:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak="break-all" display="inline">
                                  {selectedEvent.mid_ground_track.properties?.orbit}
                                </Text>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  Mode:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak="break-all" display="inline">
                                  {selectedEvent.mode_name}
                                </Text>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  Start:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak="break-all" display="inline">
                                  {formatUTC(selectedEvent.time_start)}
                                </Text>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  End:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak="break-all" display="inline">
                                  {formatUTC(selectedEvent.time_end)}
                                </Text>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  Rate:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak='break-all' display="inline">
                                  {selectedEvent.coverage_rate} %
                                </Text>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  Clouds:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak='break-all' display="inline">
                                  {typeof selectedEvent.clouds === 'number' ? `${selectedEvent.clouds} % Probability` : 'No data available'}
                                </Text>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  Resolution:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak='break-all' display="inline">
                                  {selectedEvent.resolution_m} m
                                </Text>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  Spectra:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak='break-all' display="inline">
                                  {selectedEvent.spectra} 
                                </Text>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  Avg SRT:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak='break-all' display="inline">
                                  {typeof selectedEvent.srt_info?.[1] === 'number' ? `${selectedEvent.srt_info?.[1]} h` : 'No data available'}
                                </Text>
                                <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                  Max SRT:
                                </Text>
                                <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak='break-all' display="inline">
                                  {typeof selectedEvent.srt_info?.[2] === 'number' ? `${selectedEvent.srt_info?.[2]} h` : 'No data available'}
                                </Text>
                              </Grid>
                            );
                          })()
                        ) : (
                          <Text variant={"results"} fontSize={theme.fontSize.info}>
                            Select or Display Coverage Event.
                          </Text>
                        )}
                        {/* Export */}
                        <Divider my={4} />
                        <VStack align="left">
                          <Button
                            onClick={onExportModalOpen}>
                            Export All Results
                          </Button>
                        </VStack>
                      </Box>
                    )}
                  </Box>
                )}
                <Modal isOpen={isExportModalOpen} onClose={onExportModalClose}>
                 <ModalOverlay />
                 <ModalContent bg={theme.colors.main.box} color="white">
                   <ModalHeader
                     fontFamily="heading" fontSize={theme.fontSize.alertName}>
                     Export Coverage Results
                   </ModalHeader>
                   <ModalCloseButton />
                   <ModalBody pb={6}>
                     <FormControl>
                       <FormLabel>
                         Insert Name
                       </FormLabel>
                       <Input
                         onChange={handleExportNameChange}
                         placeholder='Feasibility_Study' />
                     </FormControl>
                     <RadioGroup mt={2} onChange={setExportOption} value={exportOption}>
                      <HStack spacing={5}>
                        <Radio value='json'>JSON</Radio>
                        <Radio value='csv'>CSV</Radio>
                        <Radio value='md'>Markdown</Radio>
                      </HStack>
                     </RadioGroup>
                   </ModalBody>
                   <ModalFooter>
                     <Button
                      onClick={() => {handleExport(responseDataCPT, exportNameInput, exportOption);
                        onExportModalClose();}}
                      variant='standard' mr={3}>
                       Export
                     </Button>
                     <Button onClick={onExportModalClose}>Cancel</Button>
                   </ModalFooter>
                 </ModalContent>
                </Modal>  
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        {/* Timeline */}         
        {tabIndex === 1 && (
        <Box
          ml={2} mt={2} mr={2}
          maxHeight="75%"
          zIndex="modal"
          sx={{
            pointerEvents: 'none',
            ".vis-timeline": {
              pointerEvents: 'auto',
            },
          }}>
          <SimpleBar
            forceVisible="y" autoHide={false}
            style={{ maxHeight: "100%", pointerEvents: 'auto' }}>
            <div ref={timelineRef} />
          </SimpleBar>
        </Box>
        )}
      </Grid>  
    </Flex>
  );
};

export default BodyCPT;